<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddLossReason from './AddLossReason.vue';
import EditLossReason from './EditLossReason.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedReason = ref({});
const loading = ref({});

const accountId = computed(() => store.getters.getCurrentAccountId);
const records = computed(() => getters['dealLossReasons/getLossReasons'].value);
const uiFlags = computed(() => getters['dealLossReasons/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedReason.value.name}?`);

const fetchList = () => {
  return store.dispatch('dealLossReasons/get', { accountId: accountId.value });
};

const openAddPopup = () => {
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = reason => {
  selectedReason.value = reason;
  showEditPopup.value = true;
};

const hideEditPopup = () => {
  showEditPopup.value = false;
  selectedReason.value = {};
};

const openDeletePopup = reason => {
  selectedReason.value = reason;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  const id = selectedReason.value.id;
  closeDeletePopup();
  loading.value[id] = true;

  try {
    await store.dispatch('dealLossReasons/delete', {
      accountId: accountId.value,
      id,
    });
    useAlert(t('DEAL_LOSS_REASONS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.errors?.[0] ||
      t('DEAL_LOSS_REASONS.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[id] = false;
  }
};

const tableHeaders = computed(() => [
  t('DEAL_LOSS_REASONS.LIST.TABLE_HEADER.NAME'),
  t('DEAL_LOSS_REASONS.LIST.TABLE_HEADER.COLOR'),
  t('DEAL_LOSS_REASONS.LIST.TABLE_HEADER.REQUIRES_JUSTIFICATION'),
]);

onBeforeMount(() => {
  fetchList();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('DEAL_LOSS_REASONS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('DEAL_LOSS_REASONS.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('DEAL_LOSS_REASONS.HEADER')"
        :description="$t('DEAL_LOSS_REASONS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('DEAL_LOSS_REASONS.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="reason in records" :key="reason.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ reason.name }}
              </span>
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center">
                <span
                  class="w-4 h-4 mr-1 border border-solid rounded rtl:mr-0 rtl:ml-1 border-n-weak"
                  :style="{ backgroundColor: reason.color }"
                />
                {{ reason.color }}
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{
                reason.requires_justification
                  ? $t('DEAL_LOSS_REASONS.LIST.YES')
                  : $t('DEAL_LOSS_REASONS.LIST.NO')
              }}
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('DEAL_LOSS_REASONS.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openEditPopup(reason)"
                />
                <Button
                  v-tooltip.top="$t('DEAL_LOSS_REASONS.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[reason.id]"
                  @click="openDeletePopup(reason)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLossReason @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLossReason :selected-reason="selectedReason" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('DEAL_LOSS_REASONS.DELETE.CONFIRM.TITLE')"
      :message="$t('DEAL_LOSS_REASONS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('DEAL_LOSS_REASONS.DELETE.CONFIRM.YES')"
      :reject-text="$t('DEAL_LOSS_REASONS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
